<script setup lang="ts">
import type { Component } from 'vue';
import 'swiper/css/navigation';
import { useBanners } from '~/composables/apostatudo/use-banners';
import { useResponsiveBannerUrl } from '~/composables/apostatudo/use-responsive-banner-url';

const { isMobile } = useLayout();
const { getLoadingStrategy } = useImageLoading();
const { getResponsiveBannerUrl } = useResponsiveBannerUrl();
const {
  homeBanners: mainSliderBanners,
  fetchBanners,
  hasHomeBanners,
} = useBanners();

const bannerContainer = ref<HTMLDivElement | null>(null);

// Intersection Observer para lazy loading no mobile
let observer: IntersectionObserver | null = null;

onMounted(async () => {
  // No desktop, carregar imediatamente
  if (!isMobile.value && !hasHomeBanners.value) {
    await fetchBanners();
  }
  // No mobile, usar Intersection Observer para lazy loading
  else if (isMobile.value && bannerContainer.value && !hasHomeBanners.value) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            fetchBanners();
            observer?.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Começa a carregar 50px antes de entrar na viewport
        threshold: 0.1, // 10% do elemento visível
      },
    );

    observer.observe(bannerContainer.value);
  }
});

onUnmounted(() => {
  observer?.disconnect();
});

// Lazy load Swiper para reduzir bundle inicial
const Swiper = shallowRef<Component | null>(null);
const SwiperSlide = shallowRef<Component | null>(null);
const Navigation = shallowRef<any>(null);
const swiperLoaded = ref(false);

onBeforeMount(async () => {
  // Dynamic import do Swiper apenas quando necessário
  const [swiperVue, swiperModules, swiperElement] = await Promise.all([
    import('swiper/vue'),
    import('swiper/modules'),
    import('swiper/element/bundle'),
  ]);

  Swiper.value = swiperVue.Swiper;
  SwiperSlide.value = swiperVue.SwiperSlide;
  Navigation.value = swiperModules.Navigation;
  swiperElement.register();
  swiperLoaded.value = true;
});

type Props = {
  rounded?: boolean;
};

defineProps<Props>();

const isBannerVisible = computed<boolean>(() => {
  return mainSliderBanners.value.length > 0;
});

function filterActionBanner(banner: { link_url: string | null }) {
  if (!banner.link_url) return '/';

  // Se for uma URL interna (começa com /), retorna como está
  if (banner.link_url.startsWith('/')) {
    return banner.link_url;
  }

  // Se for uma URL externa, verifica se é da Apostatudo
  if (
    banner.link_url.startsWith('http://') ||
    banner.link_url.startsWith('https://')
  ) {
    try {
      const url = new URL(banner.link_url);

      // Se o hostname contém 'apostatudo.bet', extrai apenas o pathname
      if (url.hostname.includes('apostatudo.bet')) {
        return url.pathname + url.search + url.hash;
      }

      // Se não for Apostatudo, retorna a URL completa (será tratada como externa)
      return banner.link_url;
    } catch {
      // Se falhar ao parsear a URL, retorna como está
      return banner.link_url;
    }
  }

  return banner.link_url;
}

function isExternalLink(linkUrl: string | null) {
  if (!linkUrl) return false;

  // Se começa com /, é interno
  if (linkUrl.startsWith('/')) return false;

  // Se é uma URL completa, verifica se é da Apostatudo
  if (linkUrl.startsWith('http://') || linkUrl.startsWith('https://')) {
    try {
      const url = new URL(linkUrl);

      // Se é Apostatudo, não é externo (será tratado como rota interna)
      return !url.hostname.includes('apostatudo.bet');
    } catch {
      return true;
    }
  }

  return false;
}

const breakpoints = {
  0: {
    slidesPerView: 1.2,
    spaceBetween: 10,
  },
  768: {
    slidesPerView: 1.55,
    spaceBetween: 16,
  },
};

// Loop sempre habilitado quando tiver slides suficientes
const shouldEnableLoop = computed(() => {
  const slideCount = mainSliderBanners.value.length;

  // Desabilita loop se tiver apenas 1 banner ou menos de 3 slides
  // (Swiper precisa de pelo menos 3x slidesPerView para loop funcionar bem)
  return slideCount >= 3;
});
</script>

<template>
  <div
    v-if="isBannerVisible"
    ref="bannerContainer"
    class="bannerClass"
    :style="!swiperLoaded ? 'min-height: 250px' : ''"
  >
    <div
      v-if="swiperLoaded"
      class="bannerWrapper"
    >
      <div class="main-banner">
        <component
          :is="Swiper"
          :autoplay="{
            delay: 6000,
            pauseOnMouseEnter: true,
            disableOnInteraction: false,
          }"
          :breakpoints="breakpoints"
          :centered-slides="true"
          class="mySwiper h-full"
          create-elements
          :loop="shouldEnableLoop"
          :modules="[Navigation]"
          :navigation="{
            prevEl: '.swiper-button-prev-custom',
            nextEl: '.swiper-button-next-custom',
          }"
          preload-images="false"
          :slides-per-group="1"
          :speed="500"
        >
          <component
            :is="SwiperSlide"
            v-for="(banner, index) in mainSliderBanners"
            :key="banner.id"
            class="h-full"
          >
            <NuxtLink
              v-if="banner"
              class="block h-full"
              :external="isExternalLink(banner.link_url)"
              no-prefetch
              :target="isExternalLink(banner.link_url) ? '_blank' : '_self'"
              :to="filterActionBanner(banner)"
            >
              <img
                :alt="banner.filename || `Banner ${index}`"
                class="rounded-xl object-contain w-full h-full"
                :class="[
                  banner?.link_url ? 'cursor-pointer' : '',
                  index > 0 ? 'swiper-lazy' : '',
                ]"
                height="324"
                :loading="getLoadingStrategy(index, 1)"
                :src="getResponsiveBannerUrl(banner.url)"
                width="731"
              />
            </NuxtLink>
          </component>
        </component>

        <div
          class="swiper-button-prev-custom navigation-button navigation-button-prev"
        >
          <NuxtIcon
            :filled="true"
            name="arrows/chevron_left"
          />
        </div>
        <div
          class="swiper-button-next-custom navigation-button navigation-button-next"
        >
          <NuxtIcon
            :filled="true"
            name="arrows/chevron_right"
          />
        </div>
      </div>
    </div>
    <div
      v-else
      class="bannerWrapper"
      style="
        min-height: 250px;
        background: rgba(255, 255, 255, 0.05);
        border-radius: 0.75rem;
      "
    ></div>
  </div>

  <!-- Placeholder invisível para Intersection Observer apenas no mobile quando não há banners -->
  <div
    v-if="!isBannerVisible && isMobile"
    ref="bannerContainer"
    aria-hidden="true"
    class="invisible h-1"
  ></div>
</template>

<style scoped lang="scss">
.bannerClass {
  @apply select-none overflow-hidden;
}

.bannerWrapper {
  @apply relative;
}

.main-banner {
  @apply relative;
  width: 100%;
}

// Swiper customizado
.mySwiper {
  @apply w-full;

  :deep(.swiper-wrapper) {
    align-items: center;
  }

  :deep(.swiper-button-next),
  :deep(.swiper-button-prev) {
    display: none;
  }

  :deep(.swiper-slide) {
    opacity: 0.4;
    z-index: 1;
    transition: all 0.4s ease;
    border-radius: 0.75rem;
    overflow: hidden;
    aspect-ratio: 1240 / 541;

    @media (min-width: 768px) {
      transform: scaleY(0.82);
    }
  }

  :deep(.swiper-slide-active) {
    opacity: 1;
    z-index: 2;
    transform: scaleY(1);
    box-shadow:
      -60px 0 40px -1px rgba(0, 0, 0, 0.9),
      60px 0 40px -1px rgba(0, 0, 0, 0.9);
  }
}

// Botões de navegação - posicionados na interseção do slide ativo com os vizinhos
.navigation-button {
  @apply absolute z-10 flex items-center justify-center
         rounded-[5px] shadow-lg text-texts font-extrabold cursor-pointer;

  width: 25px;
  height: 25px;
  top: 50%;
  transform: translateY(-50%);
  background: var(
    --ATNB---ORANGE,
    radial-gradient(168.3% 138% at 36.76% -16.67%, #ff704a 0%, #ff3400 100%)
  );

  &.navigation-button-prev {
    left: 5%;
  }

  &.navigation-button-next {
    right: 5%;
  }

  @media (min-width: 640px) {
    width: 32px;
    height: 32px;
  }

  // Desktop: setas na interseção entre slide ativo e vizinhos
  // Com slidesPerView 1.55, o slide ativo ocupa ~64.5% da largura,
  // restando ~17.7% de cada lado. Setas ficam em ~17%.
  @media (min-width: 768px) {
    width: 36px;
    height: 36px;

    &.navigation-button-prev {
      left: 16%;
    }

    &.navigation-button-next {
      right: 16%;
    }
  }
}
</style>
